// useDynamicColumns.tsx

import React, { useState } from 'react';
import { NameEmailAvatar } from '../../../../../ui';

// Types
export interface Column {
  title: string;
}

// Core columns that cannot be removed
const CORE_COLUMNS = [
  'Applicant',
  'CV Score',
  'Status',
  'Location',
  'Experience',
  'Age',
  'Gender',
  'Skills',
];

// Avatar color palette
const AVATAR_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-800' },
  { bg: 'bg-purple-100', text: 'text-purple-800' },
  { bg: 'bg-green-100', text: 'text-green-800' },
  { bg: 'bg-pink-100', text: 'text-pink-800' },
  { bg: 'bg-indigo-100', text: 'text-indigo-800' },
  { bg: 'bg-teal-100', text: 'text-teal-800' },
  { bg: 'bg-orange-100', text: 'text-orange-800' },
  { bg: 'bg-cyan-100', text: 'text-cyan-800' },
  { bg: 'bg-emerald-100', text: 'text-emerald-800' },
  { bg: 'bg-violet-100', text: 'text-violet-800' },
];

const STATUS_BADGE: Record<string, { bg: string; text: string; dot: string }> =
  {
    New: {
      bg: 'bg-cyan-50 border border-cyan-200',
      text: 'text-cyan-700',
      dot: 'bg-cyan-500',
    },
    Suitable: {
      bg: 'bg-blue-50 border border-blue-200',
      text: 'text-blue-700',
      dot: 'bg-blue-500',
    },
    Outstanding: {
      bg: 'bg-purple-50 border border-purple-200',
      text: 'text-purple-700',
      dot: 'bg-purple-500',
    },
    Shortlisted: {
      bg: 'bg-emerald-50 border border-emerald-200',
      text: 'text-emerald-700',
      dot: 'bg-emerald-500',
    },
    'Auto-Assessment': {
      bg: 'bg-amber-50 border border-amber-200',
      text: 'text-amber-700',
      dot: 'bg-amber-500',
    },
    'Unsuccessful-Pending': {
      bg: 'bg-orange-50 border border-orange-200',
      text: 'text-orange-700',
      dot: 'bg-orange-500',
    },
    'Unsuccessful-Closed': {
      bg: 'bg-red-50 border border-red-200',
      text: 'text-red-700',
      dot: 'bg-red-500',
    },
  };

const getScoreColor = (num: number): { bar: string; text: string } => {
  if (num > 90) return { bar: 'bg-green-500', text: 'text-green-700' };
  if (num > 70) return { bar: 'bg-blue-500', text: 'text-blue-700' };
  if (num > 50) return { bar: 'bg-orange-500', text: 'text-orange-700' };
  return { bar: 'bg-red-500', text: 'text-red-700' };
};

// Custom Hook
export const GetColumns = () => {
  const [columns, setColumns] = useState<Column[]>([
    { title: 'Applicant' },
    { title: 'CV Score' },
    { title: 'Status' },
    { title: 'Location' },
    { title: 'Experience' },
    { title: 'Age' },
    { title: 'Gender' },
    { title: 'Skills' },
  ]);

  const getColumns = columns.map((col) => ({
    key: col.title,
    label: col.title,
    width:
      col.title === 'Applicant'
        ? '230px'
        : col.title === 'Status'
          ? '160px'
          : col.title === 'Location'
            ? '160px'
            : '110px',
    align: 'left' as const,
    render: (row: any) => {
      // --- Applicant: avatar + name + email ---
      if (col.title === 'Applicant') {
        const name = (row['Applicant'] as string) || '';
        const email = (row['Email'] as string) || '';
        const avatar = (row['Avatar'] as string) || '';
        return (
          <div className='flex items-center gap-2.5 py-0.5'>
            <NameEmailAvatar avatar={avatar} name={name} email={email} />
          </div>
        );
      }

      // --- CV Score: colored score chip ---
      if (col.title === 'CV Score') {
        const num = parseFloat(row['CV Score'] as string);
        const { bar, text } = getScoreColor(isNaN(num) ? 0 : num);
        return (
          <div className='flex items-center gap-2'>
            <div className='w-20 h-1.5 rounded-full bg-gray-200 overflow-hidden'>
              <div
                  className={`h-full rounded-full ${bar}`}
                  style={{ width: `${Math.min(isNaN(num) ? 0 : num, 100)}%` }}
              />
            </div>
            <span className={`text-xs font-semibold ${text}`}>
              { row['CV Score'] }
            </span>
          </div>
        );
      }

      // --- Status: colored pill badge ---
      if (col.title === 'Status') {
        const status = (row['Status'] as string) || '';
        const badge = STATUS_BADGE[status] || {
          bg: 'bg-gray-50 border border-gray-200',
          text: 'text-gray-600',
          dot: 'bg-gray-400',
        };
        const label = status.replace('Unsuccessful-', 'Unsuccessful\u00A0');
        return (
          <div className='flex justify-start text-left '>
            <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${badge.bg} ${badge.text}`}
            >
              <span
                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${badge.dot}`}
              />
              { label || '—' }
            </span>
          </div>
        );
      }

      // --- Location: city + country stacked ---
      if (col.title === 'Location') {
        const city = (row['City'] as string) || '';
        const country = (row['Country'] as string) || '';
        if (!city && !country)
          return <span className='text-sm text-gray-400'>—</span>;
        return (
          <div className='flex justify-start text-left '>
            <div className='text-sm leading-tight'>
              { city && (
                <div className='text-gray-800 font-medium truncate'>{ city }</div>
              ) }
              { country && (
                <div className='text-xs text-gray-400 truncate'>{ country }</div>
              ) }
            </div>
          </div>
        );
      }

      // --- Skill columns (dynamic): Yes/No icons ---
      if (!CORE_COLUMNS.includes(col.title)) {
        const val = row[col.title];
        return val === 'Yes' ? (
          <div className='flex justify-start text-left '>

            <span className='inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-600 text-xs font-bold'>
              ✓
            </span>
          </div>
        ) : (
          <div className='flex justify-start text-left '>

            <span className='inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-400 text-xs font-bold'>
              ✕
            </span>
          </div>
        );
      }

      // --- Default plain text ---
      const value = row[col.title];
      return (
        <div className='flex justify-start text-left '>
          <span className='text-sm text-gray-700'>{ value || '—' }</span>
        </div>
      );
    },

    disableSort: !['CV Score', 'Experience', 'Age', 'Skills'].includes(
      col.title,
    ),
  }));

  return { columns, setColumns, getColumns };
};
